import {
  Form,
  message,
  Button,
  InputNumber,
  DatePicker,
  Select,
  Typography,
  Modal,
  Input,
  Tabs,
  Alert,
  Tooltip,
  Switch,
  theme,
} from "antd";
import { ApiService } from "api/api.service";
import { LoanFrequencyMultiplier } from "components/Application/constants";
import dayjs from "dayjs";
import { T_Populate } from "types/_utils.types";
import { FileSchemaDto } from "dtos/file.dto";
import { FREQUENCY_TYPES, LoanSchemaDto } from "dtos/loan.dto";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { TbUpload, TbFileTypePdf, TbX, TbAlertCircle, TbChevronDown, TbInfoCircle } from "react-icons/tb";
import { toTitleCase, getContentTypeFromFile, formatBytes } from "utils/functions";
import { queryClient } from "utils/queryClient";
import { InfoCircleOutlined } from "@ant-design/icons";

const allowedMimeTypes = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const allowedExtensions = new Set(["pdf", "doc", "docx"]);

interface CreditorModalProps {
  selectedLoan?: T_Populate<
    LoanSchemaDto,
    {
      attachments: FileSchemaDto[];
    }
  >;
  open: boolean;
  onClose: () => void;
  appkey: string;
  source?: "create-application" | "application-page";
  isPlanSelected?: boolean;
  isPriorApplication?: boolean;
}

type TabKey = "details" | "upload";

export const AddLoanModal = ({
  onClose,
  open,
  selectedLoan,
  appkey,
  source = "application-page",
  isPlanSelected,
  isPriorApplication = false,
}: CreditorModalProps) => {
  const editLoan = ApiService.upstream.useEditLoan();
  const addLoan = ApiService.upstream.useAddLoan();
  const addLoanByFile = ApiService.upstream.useAddLoanByFile();

  const [activeTab, setActiveTab] = useState<TabKey>("details");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const [prevFrequency, setPrevFrequency] = useState<FREQUENCY_TYPES | undefined>(undefined);
  const [prevDueAmount, setPrevDueAmount] = useState<number | undefined>(undefined);
  const [prevPaymnets, setPrevPaymnets] = useState<number | undefined>(undefined);

  const [creditorForm] = Form.useForm();
  const autocalculate = Form.useWatch("autocalculate", creditorForm);

  const overriddenFields = useRef<Set<string>>(new Set());
  const markOverridden = (field: string) => () => {
    overriddenFields.current.add(field);
  };

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollHint = useCallback(() => {
    if (!scrollEl) return;
    setCanScrollDown(scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight > 8);
  }, [scrollEl]);

  useEffect(() => {
    if (!scrollEl) return;
    updateScrollHint();
    const observer = new ResizeObserver(updateScrollHint);
    observer.observe(scrollEl);
    if (scrollEl.firstElementChild) observer.observe(scrollEl.firstElementChild);
    return () => observer.disconnect();
  }, [scrollEl, updateScrollHint]);

  const scrollDown = () => {
    scrollEl?.scrollBy({ top: scrollEl.clientHeight * 0.8, behavior: "smooth" });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const mismatchedFileTypes = fileArray.filter((file) => {
        if (file.type && allowedMimeTypes.has(file.type)) {
          return false;
        }
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !allowedExtensions.has(ext);
      });

      if (mismatchedFileTypes.length > 0) {
        message.error("Only PDF and DOC files are allowed");
        return;
      }

      const oversizedFiles = fileArray.filter((file) => file.size > 25 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        message.error(`Some files exceed 25MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`);
        return;
      }

      setAttachedFiles((prev) => [...prev, ...fileArray]);
    }

    event.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  const formatter = (value: number | undefined) => {
    if (value === undefined) return "";
    const [start, end] = `${value}`.split(".") || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${end ? `${v}.${end}` : `${v}`}`;
  };

  const roundedFormatter = (value: number | undefined, info: { userTyping: boolean }) => {
    if (value === undefined || `${value}` === "" || info.userTyping) return formatter(value);
    const num = Number(value);
    return formatter(Number.isFinite(num) ? round2(num) : value);
  };

  const parser = (value: string | undefined) => value?.replace(/\$\s?|(,*)/g, "") as unknown as number;

  const handleClose = () => {
    onClose();
    setActiveTab("details");
    setAttachedFiles([]);
  };

  const handleLoanFrequencyChange = ({
    newFrequency,
    prevFrequency,
    record,
  }: {
    prevFrequency: FREQUENCY_TYPES | undefined;
    newFrequency: FREQUENCY_TYPES | undefined;
    record: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>;
  }) => {
    const payments = record?.payments ?? 0;
    const frequencyChanged = !!prevFrequency && !!newFrequency && prevFrequency !== newFrequency;
    const newPaybackAmount = round2(
      frequencyChanged
        ? (payments / LoanFrequencyMultiplier[prevFrequency]) * LoanFrequencyMultiplier[newFrequency]
        : payments,
    );

    const updatedLoan = {
      ...record,
      frequency: newFrequency,
      payments: newPaybackAmount,
      manual_overrides: Array.from(overriddenFields.current),
    };
    const isFieldChanged =
      prevDueAmount !== record.due_amount || prevFrequency !== record.frequency || prevPaymnets !== record.payments;

    editLoan.mutate(
      { appkey, loan: updatedLoan as any, source: "application-page", isFieldsChanged: isFieldChanged },
      {
        onSuccess: (res) => {
          const editedLoan = res.result.editedLoan;
          message.success("Loan edited succesfully!");
          creditorForm.setFieldsValue({
            ...editedLoan,
            additional_fees:
              round2((editedLoan.origination_fees ?? 0) + (editedLoan.additional_fees_deductions ?? 0)) || undefined,
          });
          setPrevFrequency(editedLoan.frequency);
          setPrevDueAmount(editedLoan.due_amount);
          setPrevPaymnets(editedLoan.payments);
          queryClient.invalidateQueries({ queryKey: ["application", appkey] });
          overriddenFields.current = new Set();
        },
      },
    );
  };

  const handleUploadSubmit = () => {
    if (attachedFiles.length === 0) {
      message.error("Please attach at least one file");
      return;
    }

    const formData = new FormData();
    attachedFiles.forEach((file) => {
      formData.append("files", file);
    });

    addLoanByFile.mutate(
      { appkey, files: formData },
      {
        onSuccess: () => {
          message.success("Loan file processing has started!");
          handleClose();
          queryClient.invalidateQueries({ queryKey: ["application", appkey] });
        },
      },
    );
  };

  useEffect(() => {
    if (selectedLoan) {
      creditorForm.resetFields();
      setActiveTab("details");
      creditorForm.setFieldsValue({
        ...selectedLoan,
        additional_fees:
          round2((selectedLoan.origination_fees ?? 0) + (selectedLoan.additional_fees_deductions ?? 0)) || undefined,
      });
      setPrevFrequency(selectedLoan.frequency);
      setPrevDueAmount(selectedLoan.due_amount);
      setPrevPaymnets(selectedLoan.payments);
      overriddenFields.current = new Set();
    } else {
      creditorForm.resetFields();
      overriddenFields.current = new Set();
    }
  }, [selectedLoan, creditorForm]);

  const footer = useMemo(() => {
    const actions =
      activeTab === "upload"
        ? [
            <Button key="cancel" onClick={handleClose}>
              Cancel
            </Button>,
            <Button
              key="upload"
              onClick={handleUploadSubmit}
              type="primary"
              disabled={attachedFiles.length === 0}
              loading={addLoanByFile.isPending}
            >
              Upload
            </Button>,
          ]
        : [
            <Button key="cancel" onClick={handleClose}>
              Cancel
            </Button>,
            <Button
              key="confirm"
              onClick={() => creditorForm.submit()}
              type="primary"
              loading={editLoan.isPending || addLoan.isPending}
            >
              {selectedLoan ? "Update Details" : "Add Details"}
            </Button>,
          ];

    return (
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {activeTab === "details" && (
            <>
              <Switch
                checked={autocalculate !== false}
                onChange={(checked) => creditorForm.setFieldValue("autocalculate", checked)}
              />
              <span className="text-sm">Autocalculate</span>
              <Tooltip title="When on, Net Purchase Price, Net Funded Amount, Additional Fees, Breakeven Amount and Balance to Total Debt % are recalculated on save. When off, values are saved exactly as entered.">
                <TbInfoCircle size={16} className="text-gray-400 cursor-help" />
              </Tooltip>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
    );
  }, [
    activeTab,
    attachedFiles.length,
    selectedLoan,
    addLoanByFile.isPending,
    editLoan.isPending,
    addLoan.isPending,
    creditorForm,
    autocalculate,
    handleClose,
    handleUploadSubmit,
  ]);

  const detailsTabContent = (
    <Form
      form={creditorForm}
      layout="vertical"
      onFinish={(formValues) => {
        const { additional_fees, ...values } = formValues;
        if (overriddenFields.current.has("additional_fees")) {
          overriddenFields.current.add("origination_fees");
          overriddenFields.current.add("additional_fees_deductions");
          values.origination_fees = additional_fees ?? null;
          values.additional_fees_deductions = 0;
        }

        if (selectedLoan) {
          handleLoanFrequencyChange({
            prevFrequency,
            newFrequency: values.frequency,
            record: values,
          });
        } else {
          addLoan.mutate(
            {
              appkey,
              loan: {
                ...values,
                payments: typeof values.payments === "number" ? round2(values.payments) : values.payments,
                manual_overrides: Array.from(overriddenFields.current),
              },
            },
            {
              onSuccess: async () => {
                message.success("Loan added succesfully!");
                creditorForm.resetFields();
                handleClose();
                await queryClient.invalidateQueries({
                  queryKey: ["application", appkey],
                  refetchType: "active",
                });
                overriddenFields.current = new Set();
              },
            },
          );
        }
      }}
    >
      <Form.Item hidden name={"_id"} />
      <Form.Item hidden name="autocalculate" valuePropName="checked" initialValue={true}>
        <Switch />
      </Form.Item>
      <Form.Item hidden name="origination_fees" />
      <Form.Item hidden name="additional_fees_deductions" />
      <Form.Item label="Creditor Name" name={"creditor_name"} rules={[{ required: true }]}>
        <Input placeholder="Creditor name" />
      </Form.Item>
      <div className="grid grid-cols-3 gap-4">
        <Form.Item
          label="Funded Amount"
          name={"funded_amount"}
          rules={[{ required: true }]}
          tooltip="Raw funded amount from the document. Never autocalculated. Used to calculate Net Purchase Price."
        >
          <InputNumber
            formatter={formatter}
            parser={parser}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Funded amount"
            onChange={markOverridden("funded_amount")}
          />
        </Form.Item>
        <Form.Item
          label="Funded Date"
          getValueProps={(value) => ({
            value: value ? dayjs(value) : null,
          })}
          normalize={(value) => (value ? value.toDate() : null)}
          name={"funded_date"}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Payback Amount" name={"payback_amount"} rules={[{ required: true }]}>
          <InputNumber
            formatter={formatter}
            parser={parser}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Payback amount"
          />
        </Form.Item>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Form.Item label="Balance Amount" name={"due_amount"} rules={[{ required: true }]}>
          <InputNumber
            formatter={formatter}
            parser={parser}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Balance amount"
          />
        </Form.Item>
        <Form.Item label="Installation Payment" name={"payments"} rules={[{ required: true }]}>
          <InputNumber
            formatter={roundedFormatter}
            parser={parser}
            precision={2}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Installation payment"
          />
        </Form.Item>
        <Form.Item label="Percentage Receivables" name={"receivables"} tooltip="Percentage of receivables">
          <InputNumber<number>
            min={0}
            max={100}
            formatter={(value) => (value ? `${value}%` : "")}
            parser={(value) => parseFloat(value?.replace("%", "") || "0")}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Form.Item
          label="Net Purchase Price"
          name="net_purchase_price"
          tooltip={
            <>
              {selectedLoan?.potential_refinance && (
                <div>This purchase may potentially be a refinance. Review the loan document.</div>
              )}
              <div>
                You may provide this manually, or leave it empty and save. The system will autocalculate it as Funded
                Amount − Additional Fees. It also recalculates when either of those two are edited.
              </div>
            </>
          }
        >
          <InputNumber
            formatter={formatter}
            parser={parser}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Net Purchase Price"
            onChange={markOverridden("net_purchase_price")}
          />
        </Form.Item>
        <Form.Item
          label="Additional Fees"
          name="additional_fees"
          tooltip="Origination fees plus any other deductions (e.g. wire fees). If left empty and Net Purchase Price is provided, this will be calculated as Funded Amount − Net Purchase Price. Edit this field directly to override."
        >
          <InputNumber
            formatter={formatter}
            parser={parser}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Additional fees"
            onChange={markOverridden("additional_fees")}
          />
        </Form.Item>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {/* Hidden for now — backend still derives net_funded_amount from Net Purchase Price.
        <Form.Item
          label="Net Funded Amount"
          name="net_funded_amount"
          tooltip="Mirrors Net Purchase Price. Used to calculate Breakeven Amount."
        >
          <InputNumber
            formatter={formatter}
            parser={parser}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Net funded amount"
            onChange={markOverridden("net_funded_amount")}
          />
        </Form.Item>
        */}
        <Form.Item
          label="Breakeven Amount"
          name="breakeven_amount"
          tooltip="The system will autocalculate it once Balance Amount is available, using Net Funded Amount × 1.1 as the breakeven base: Net Funded Amount × 1.1 − (Payback Amount − Balance Amount). Recalculated on save when Autocalculate is on. Edit this field directly to override."
        >
          <InputNumber
            formatter={formatter}
            parser={parser}
            prefix="$"
            style={{ width: "100%" }}
            placeholder="Breakeven amount"
            onChange={markOverridden("breakeven_amount")}
          />
        </Form.Item>
        <Form.Item
          label="Balance to Total Debt of this loan"
          name="balance_to_totaldebt_percentage"
          tooltip="The system will autocalculate it once Balance Amount is available: (Balance Amount ÷ Payback Amount) × 100. Recalculated on save when Autocalculate is on. Edit this field directly to override."
        >
          <InputNumber<number>
            min={0}
            max={100}
            formatter={(value) => (value ? `${value}%` : "")}
            parser={(value) => parseFloat(value?.replace("%", "") || "0")}
            style={{ width: "100%" }}
            placeholder="Balance / Total Debt"
            onChange={markOverridden("balance_to_totaldebt_percentage")}
          />
        </Form.Item>
        <Form.Item label="Frequency" name={"frequency"}>
          <Select
            defaultValue={FREQUENCY_TYPES.WEEKLY}
            options={Object.values(FREQUENCY_TYPES).map((val) => {
              return { label: toTitleCase(val), value: val };
            })}
            placeholder="Frequency"
          />
        </Form.Item>
      </div>
    </Form>
  );

  const uploadTabContent = (
    <>
      <label
        htmlFor="creditor-upload-file"
        className="bg-white text-slate-500 font-semibold text-base rounded w-full h-52 flex flex-col items-center justify-center cursor-pointer border-2 border-gray-300 border-dashed mx-auto"
      >
        <TbUpload size={44} className="mb-3 text-gray-500" />
        Upload file
        <input
          onChange={handleFileSelect}
          type="file"
          multiple
          id="creditor-upload-file"
          accept=".pdf,.doc,.docx"
          className="hidden"
        />
        <p className="text-xs font-medium text-slate-400 mt-2">PDF and DOC files are allowed.</p>
      </label>
      <div className="space-y-3 max-h-[320px] overflow-y-auto mt-4">
        {attachedFiles.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <Typography.Text strong className="text-sm">
                {attachedFiles.length} file(s) attached
              </Typography.Text>
            </div>
            <div className="space-y-2">
              {attachedFiles.map((file, index) => {
                const type = getContentTypeFromFile(file);
                return (
                  <div key={index} className="flex items-center justify-between p-2 rounded transition-colors bg-white">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {type === "pdf" ? <TbFileTypePdf /> : null}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-700 truncate">{file.name}</div>
                        <div className="text-xs text-gray-500">{formatBytes(file.size)}</div>
                      </div>
                    </div>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<TbX size={16} />}
                      onClick={() => handleRemoveFile(index)}
                      className="flex-shrink-0"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );

  const tabItems = [
    {
      key: "details" as const,
      label: "Enter Details",
      children: detailsTabContent,
    },
    ...(selectedLoan
      ? []
      : [
          {
            key: "upload" as const,
            label: "Upload Document",
            children: uploadTabContent,
          },
        ]),
  ];

  return (
    <Modal
      destroyOnHidden
      title={selectedLoan?.creditor_name ?? "Add Creditor"}
      open={open}
      onCancel={handleClose}
      footer={footer}
      width={900}
      centered
      styles={{
        content: {
          padding: 24,
        },
      }}
    >
      <div className="relative">
        <div ref={setScrollEl} onScroll={updateScrollHint} className="overflow-y-auto pr-2" style={{ height: 480 }}>
          <div className="pb-4">
            <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as TabKey)} items={tabItems} />
          </div>
        </div>
        {canScrollDown && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 flex h-16 items-end justify-center pb-1"
            style={{ background: "linear-gradient(to top, #ffffff 35%, rgba(255, 255, 255, 0))" }}
          >
            <Button
              className="pointer-events-auto"
              size="small"
              shape="round"
              icon={<TbChevronDown />}
              iconPosition="end"
              onClick={scrollDown}
            >
              Scroll for more
            </Button>
          </div>
        )}
      </div>

      {source === "application-page" && isPlanSelected && !isPriorApplication && (
        <Alert
          icon={<TbAlertCircle size={20} />}
          className="mt-4"
          type="warning"
          message="Adding/editing a loan will reset your current selected strategy if application has not yet signed. Please re-select your preferred strategy after adding/editing the loan."
        />
      )}
    </Modal>
  );
};
